/**
 * 검색어가 **정산년월처럼 생겼는데** 선택기가 다른 달로 좁혀져 있을 때 알린다.
 *
 * 왜 (2026-08-02): 검색에 `drive_mon` 을 넣어 `2026-08` 로도 찾을 수 있게 했는데,
 * 그 화면들은 **정산년월 선택기도** 갖고 있다. 둘은 AND 로 걸리므로 선택기가 `2026-06` 인 채로
 * `2026-08` 을 치면 **아무 잘못 없이 0건**이 뜬다 — 축이 둘이면 서로를 죽인다는 그 자리다.
 * 막지 않고 **그 이유를 그 자리에 적고 한 번에 고칠 버튼**을 준다.
 *
 * ⚠️ 조용히 선택기를 바꾸지 않는다. 사용자가 고른 달을 코드가 대신 바꾸면 그 다음 화면들도
 *    따라 바뀐다(정산년월은 화면 간에 기억된다) — 되돌릴 방법을 모르는 변경이 된다.
 */
export default function MonthSearchHint({
  q,
  mon,
  months,
  onPick,
  /** 있으면 "전체 기간" 버튼도 낸다. 급여명세처럼 전체 조회가 무거운 화면은 안 준다. */
  onAll,
}: {
  q: string;
  mon: string;
  months: readonly string[];
  onPick: (m: string) => void;
  onAll?: () => void;
}) {
  const t = q.trim();
  // `2026` · `2026-8` · `2026-08` 만 월로 본다. 그 밖은 이름·메모 검색이라 참견하지 않는다.
  if (!/^\d{4}(-\d{1,2})?$/.test(t)) return null;
  // 선택기가 '전체'면 검색이 그대로 먹는다.
  if (!mon) return null;
  // 고른 달이 이미 검색어에 걸리면 조용히 있는다(`2026` 을 치고 `2026-06` 을 보고 있는 경우).
  if (mon.startsWith(t)) return null;

  const hits = months.filter((m) => m.startsWith(t));

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
      <span>
        ‘<b>{t}</b>’ 로 찾고 있는데 정산년월이 <b>{mon}</b> 으로 좁혀져 있어 결과가 비어 보일 수 있습니다.
      </span>
      {hits.slice(0, 3).map((m) => (
        <button
          key={m}
          type="button"
          className="rounded bg-white px-2 py-0.5 font-medium text-amber-800 hover:bg-amber-100"
          onClick={() => onPick(m)}
        >
          {m} 으로 보기
        </button>
      ))}
      {hits.length === 0 && <span className="text-amber-800">— 그 달은 목록에 없습니다.</span>}
      {onAll && (
        <button
          type="button"
          className="rounded bg-white px-2 py-0.5 font-medium text-amber-800 hover:bg-amber-100"
          onClick={onAll}
        >
          전체 기간
        </button>
      )}
    </div>
  );
}
